import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'default' | 'fairy' | 'neon';

interface ThemeState {
  mode: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'default',
      setTheme: (mode) => set({ mode }),
    }),
    {
      name: 'theme-storage',
    }
  )
);

// Theme configurations
export const themeConfig = {
  neon: {
    primary: '#00FF00', // Neon green
    secondary: '#00CC00', // Darker neon green
    background: '#000000', // Black
    card: '#0A0A0A', // Slightly lighter black
    text: '#00FF00', // Neon green text
    accent: '#00FF00', // Neon green accent
    gradients: {
      primary: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)',
      card: 'linear-gradient(135deg, rgba(10, 10, 10, 0.8) 0%, rgba(0, 0, 0, 0.8) 100%)',
      glow: 'radial-gradient(circle at center, rgba(0, 255, 0, 0.15) 0%, transparent 70%)',
      sparkle: 'linear-gradient(45deg, transparent 0%, rgba(0, 255, 0, 0.1) 50%, transparent 100%)',
      header: 'linear-gradient(180deg, rgba(10, 10, 10, 0.95) 0%, rgba(0, 0, 0, 0.95) 100%)',
      player: 'linear-gradient(0deg, rgba(0, 0, 0, 0.98) 0%, rgba(10, 10, 10, 0.98) 100%)'
    },
    shadows: {
      primary: '0 4px 14px 0 rgba(0, 255, 0, 0.3)',
      card: '0 4px 14px 0 rgba(0, 0, 0, 0.5)',
      glow: '0 0 20px rgba(0, 255, 0, 0.2)',
      sparkle: '0 0 8px rgba(0, 255, 0, 0.3)'
    },
    borders: {
      glow: '1px solid rgba(0, 255, 0, 0.2)',
      card: '1px solid rgba(0, 255, 0, 0.1)'
    }
  },
  fairy: {
    primary: '#B8A5D1', // Main lavender
    secondary: '#9B8BB9', // Darker lavender
    background: '#1a1625', // Deep purple background
    card: '#2a2435', // Lighter card background
    text: '#E6D7FF', // Light lavender text
    accent: '#FFE6FF', // Light pink accent
    gradients: {
      primary: 'linear-gradient(135deg, #B8A5D1 0%, #9B8BB9 100%)',
      card: 'linear-gradient(135deg, rgba(42, 36, 53, 0.8) 0%, rgba(26, 22, 37, 0.8) 100%)',
      glow: 'radial-gradient(circle at center, rgba(184, 165, 209, 0.15) 0%, transparent 70%)',
      sparkle: 'linear-gradient(45deg, transparent 0%, rgba(255, 230, 255, 0.1) 50%, transparent 100%)',
      header: 'linear-gradient(180deg, rgba(42, 36, 53, 0.95) 0%, rgba(26, 22, 37, 0.95) 100%)',
      player: 'linear-gradient(0deg, rgba(26, 22, 37, 0.98) 0%, rgba(42, 36, 53, 0.98) 100%)',
    },
    shadows: {
      primary: '0 4px 14px 0 rgba(184, 165, 209, 0.3)',
      card: '0 4px 14px 0 rgba(26, 22, 37, 0.5)',
      glow: '0 0 20px rgba(184, 165, 209, 0.2)',
      sparkle: '0 0 8px rgba(255, 230, 255, 0.3)',
    },
    borders: {
      glow: '1px solid rgba(184, 165, 209, 0.2)',
      card: '1px solid rgba(184, 165, 209, 0.1)',
    }
  },
  default: {
    primary: '#FEAA2D',
    secondary: '#E69819',
    background: '#111827',
    card: '#1F2937',
    text: '#FFFFFF',
    accent: '#FEAA2D',
    gradients: {
      primary: 'linear-gradient(135deg, #FEAA2D 0%, #E69819 100%)',
      card: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)',
      glow: 'none',
      sparkle: 'none',
      header: 'none',
      player: 'none',
    },
    shadows: {
      primary: '0 4px 14px 0 rgba(254, 170, 45, 0.2)',
      card: '0 4px 14px 0 rgba(0, 0, 0, 0.5)',
      glow: 'none',
      sparkle: 'none',
    },
    borders: {
      glow: 'none',
      card: 'none',
    }
  }
}; 